/* eslint-disable prettier/prettier */
import * as React from 'react';
import { useAuth, authClient } from 'oasis-auth';
import { readGlobal, getLogger } from 'oasis-os-common';
import { useLocation } from 'react-router-dom';
import { Redirect } from 'react-router';
import { useAppState } from 'oasis-os-react';
import { ContentfulApp, LocationState, OnBoarding } from '../../types';
import { getSilentInfo } from '../../utils/getSilentInfo';
import { getSmartRedirect } from '../../utils/getSmartRedirect';
import { Deeplink } from '../Deeplink';
import { ClientContext } from 'oasis-os-contentful';
import { dispatch as dispatcher} from 'oasis-os-react';

const logger = getLogger(PKG_NAME, PKG_VERSION, 'Login');

export default function Login() {
  const { callback } = React.useContext(ClientContext);
  const [config = {} as ContentfulApp, ,] = useAppState<ContentfulApp>();
  const { fields: appConfig } = config;
  const [auth, dispatch] = useAuth();
  const { state } = useLocation<LocationState>();
  const authConfig = readGlobal('$.global.value.authConfig');
  const onboarding: OnBoarding = readGlobal('$.global.value.functionalConfig.onboarding');
  /**
   * isDeepLinkEnabled -> flag to know if deeplink is enabled
   */
  const isDeepLinkEnabled: boolean = readGlobal(
    '$.global.value.functionalConfig.features.deeplink.enabled',
  );
  const deeplink = isDeepLinkEnabled && !!sessionStorage.getItem('link');

  const [isCallbackComplete, setIsCallbackComplete] = React.useState(false);
  const isMultiTenantFlow : boolean = readGlobal(
    '$.global.value.authConfig.auth.multiTenant',
  ) || false;

  const shouldRedirect = auth.authenticated &&
  ((!!isMultiTenantFlow && isCallbackComplete) ||
   ((!isMultiTenantFlow) && !isCallbackComplete));

  React.useEffect(() => {
    async function authenticate() {
      try {
        let silentData: string | null = null;

        if (sessionStorage.getItem('silentAuth')) {
          silentData = JSON.parse(sessionStorage.getItem('silentAuth'));
          sessionStorage.removeItem('silentAuth');
        }

        const customHeadersObj = getSilentInfo('customHeaders', authConfig.auth.customHeaders);
        const customHeaders = customHeadersObj.hasOwnProperty('customHeaders')
          ? customHeadersObj.customHeaders
          : customHeadersObj;

        // clientUrl field expect to have host url without ending slash like
        // "https://pwa-core.dev.wlpc.cloud.synchronoss.net"
        // PUBLIC_PATH env var should be configured on build step
        // and have starting and ending slashes like
        // export PUBLIC_PATH="/web/"
        const client = await authClient.init(
          {
            silentAuth: silentData,
            ...authConfig.auth,
            clientUrl: authConfig?.auth.clientUrl + (process.env.PUBLIC_PATH || ''),
            customHeaders,
          },
          dispatch,
          authConfig?.auth?.apiConfig ?? [],
          process.env.PUBLIC_PATH,
        );

        if (client) {
          await client.authenticate();
        }
      } catch (e) {
        if (process.env.NODE_ENV !== 'production') {
          logger.error(e, 'ERROR AUTHENTICATING');
        }
      }
    }

    if (!auth.authenticated && authConfig?.auth) {
      authenticate();
    }
  }, [authConfig]);

  React.useEffect(() => {
    async function handleCallback() {
      if(!auth?.session?.tenantId) {
        setIsCallbackComplete(true);
      }else if (auth?.session?.tenantId) {
        await callback(auth?.session?.tenantId);
        setIsCallbackComplete(true);
      }
    }
    auth.authenticated && !!isMultiTenantFlow && handleCallback();
  }, [auth.authenticated, auth?.session?.tenantId]);
  
  React.useEffect(()=>{
    if(shouldRedirect){
      dispatcher('*', 'showSpinner', false);
    }
  }, [shouldRedirect])
    
    if (shouldRedirect) {
      if (onboarding?.isEnabled && onboarding?.route) {
        return <Redirect to={{ pathname: onboarding.route, state: { from: state?.from } }} />;
      }
    
      if (deeplink) {
        return <Deeplink config={{ ...appConfig?.config?.fields, redirectPath: state?.from }} />;
      }
    
      const smartRedirect = getSmartRedirect();
      if (smartRedirect) {
        return <Redirect to={{ pathname: smartRedirect }} />;
      }
      return <Redirect to={state?.from ?? { pathname: '/' }} />;
    }
  
   return null;
}
